import React, { useState } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Collapse from '@material-ui/core/Collapse';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import ConditionalActionWrapper from 'UI/components/atoms/ConditionalWrapper';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import FPHint from 'UI/components/atoms/FPHint';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import { SvgArrowDown, SvgArrowRight, SvgArrowUp } from 'UI/res/icons/filled';

import {
  cardStyles,
  useActionStyles,
  useContentStyles,
  useFooterStyles,
  useHeaderStyles
} from './styles';

/** @typedef {import('@material-ui/core').CardContentProps} CardContentProps */
/** @typedef {import('@material-ui/core').CardHeaderProps} CardHeaderProps */
/** @typedef {import('@material-ui/core').CollapseProps} CollapseProps */

/** CARD STYLE CUSTOMIZATIONS */
export const FPCard = withStyles(cardStyles)(Card);

/** CARD HEADER CUSTOMIZATIONS */

const CARD_HEADER_VARIANTS = {
  regular: 'regular',
  hero: 'hero'
};

export const FPCardHeader = ({
  action,
  actionMode,
  expanded,
  hintProps,
  title,
  variant,
  ...rest
}) => {
  const classes = useHeaderStyles();
  const classesAction = useActionStyles();

  const ExpandAction = (
    <FPIconButton icon={expanded ? SvgArrowDown : SvgArrowUp} iconProps={{ size: 'sm' }} />
  );

  const actionTypes = {
    actionAndExpand: (
      <Grid container className={classesAction.actionAndExpandContainer}>
        {action && <Grid item>{action}</Grid>}
        <Grid item>{ExpandAction}</Grid>
      </Grid>
    ),
    default: ExpandAction,
    custom: action,
    noAction: ''
  };

  const isCustomAction = action && actionMode !== 'actionAndExpand';

  const isRegularCardVariant = variant === CARD_HEADER_VARIANTS.regular;

  return (
    <CardHeader
      classes={{
        root: clsx(classes.defaultBackground, !isRegularCardVariant && classes.headerPadding),
        title: isRegularCardVariant && classes.regularHeaderTitle,
        subheader: clsx(
          isRegularCardVariant && null,
          !isRegularCardVariant && classes.subHeaderColor,
          variant === CARD_HEADER_VARIANTS.hero && classes.subHeadHeroPadding
        )
      }}
      action={isCustomAction ? actionTypes.custom : actionTypes[actionMode]}
      title={
        <>
          {title} {hintProps ? <FPHint position="top" {...hintProps} /> : null}
        </>
      }
      {...rest}
    />
  );
};

FPCardHeader.defaultProps = {
  variant: CARD_HEADER_VARIANTS.hero,
  actionMode: 'noAction',
  hintProps: undefined,
  expanded: true
};

/** CARD CONTENT CUSTOMIZATIONS */

export const FPCardContent = ({
  canBeExpanded,
  children,
  collapseProps,
  footerProps: { actionButtonProps, footerActionIcon, footerActionText, customClass, CustomFooter },
  headerProps,
  variant,
  ...rest
}) => {
  const classes = useContentStyles();
  const footerClasses = useFooterStyles();

  const [expanded, setExpanded] = useState(true);

  const handleExpandClick = () => {
    if (!canBeExpanded) return;
    setExpanded(!expanded);
  };

  const cardContentClasses = {
    tight: classes.tight,
    regular: classes.regular,
    relaxed: classes.relaxed
  };

  return (
    <>
      {headerProps && (
        <FPCardHeader expanded={expanded} onClick={handleExpandClick} {...headerProps} />
      )}
      <ConditionalActionWrapper
        condition={canBeExpanded}
        wrapper={(c = children) => (
          <Collapse in={expanded} {...collapseProps}>
            {c}
          </Collapse>
        )}
      >
        <CardContent className={cardContentClasses[variant]} {...rest}>
          {children}
          {footerActionText && (
            <div className={clsx(footerClasses.buttonContainer, customClass)}>
              <FPActionButton
                variant="text"
                size="small"
                icon={footerActionIcon || SvgArrowRight}
                iconPosition="right"
                iconProps={{ size: 12 }}
                text={footerActionText}
                {...actionButtonProps}
              />
            </div>
          )}
          {CustomFooter}
        </CardContent>
      </ConditionalActionWrapper>
    </>
  );
};

FPCardContent.defaultProps = {
  variant: 'regular',
  canBeExpanded: false,
  headerProps: undefined,
  footerProps: {
    footerActionText: '',
    actionButtonProps: {}
  }
};
